import { isApprovedUrl, MarkdownWrapper } from '@bob/library'
import { AppProviderContext } from '@websrc/App/AppContext'
import { marked, Renderer } from 'marked'
import React, { ComponentPropsWithRef, forwardRef, useContext, useMemo } from 'react'

import { revertMargin } from './Markdown.css'

export interface MarkdownProps extends Omit<ComponentPropsWithRef<'div'>, 'children'> {
  markdown: string
  color?: string
  smallBody?: boolean
  center?: boolean
  headerIds?: boolean
  skipWidth?: boolean
}

export const Markdown = forwardRef<HTMLDivElement, MarkdownProps>(
  ({ markdown, headerIds = true, skipWidth, ...divProps }, ref) => {
    const { locale, pageConfigType } = useContext(AppProviderContext)

    const html = useMemo(() => {
      const cnURL = process.env.DOT_CN_SITE_URL || ''
      const comURL = process.env.DOT_COM_SITE_URL || ''

      // Assets base URL must be defined. Defaulting to localhost only relevant for tests.
      const assetsBaseUrl = process.env.ASSETS_BASE_URL || 'https://localhost'

      marked.setOptions({
        gfm: true,
        breaks: true,
        mangle: false,
        silent: true,
      })

      const renderer: Partial<Renderer> = {
        heading(text, level) {
          return `<h${level} class="${revertMargin} markdown-heading">${text}</h${level}>`
        },
        paragraph(text) {
          return `<p class="${revertMargin} markdown-paragraph">${text}</p>`
        },
        del: (text) => `~${text}~`,
        checkbox(checked) {
          return `<input type="checkbox" ${checked ? 'checked' : ''} />`
        },
        list(body, ordered) {
          const tag = ordered ? 'ol' : 'ul'
          return `<${tag} class="markdown-list">${body}</${tag}>`
        },
        listitem(text) {
          const IsCheckbox = text.startsWith('<input type="checkbox"')
          return `<li class="${
            IsCheckbox ? 'is-checkbox markdown-list' : 'markdown-list'
          }">${text}</li>`
        },
        link(href, _, text) {
          let renderRef = '#'
          let isAsset = false
          const trackingId = 'markdown-link'
          const isApproved = isApprovedUrl(href ?? '')

          if (href) {
            const search = href.includes('?') ? href.split('?')[1] : ''
            const modalId = new URLSearchParams(search)?.get('modalId')
            const assetRegEx = /^https\:\/\/(images|assets).contentstack.io/g
            isAsset = assetRegEx.test(href)
            renderRef = href.replace(assetRegEx, assetsBaseUrl)

            // If asset, set locale parameter.
            if (isAsset) {
              try {
                const url = new URL(renderRef)
                url.searchParams.set('locale', locale)
                renderRef = url.href
              } catch (_) {
                console.warn(`Markdown: Invalid asset link: ${renderRef}`)
              }
            }

            // if we are in cn and the url start with com, replace it
            if (pageConfigType === 'dot-cn' && href.startsWith(comURL)) {
              renderRef = href.replace(comURL, cnURL)
            }

            if (modalId) {
              return `<a href="" data-modal-id="${modalId}" data-t-id="${trackingId}">${text}</a>`
            }
          }

          const targetBlank =
            !isApproved || isAsset ? 'target="_blank" rel="noreferrer noopener"' : ''

          return `<a href="${renderRef}" data-t-id="${trackingId}" ${targetBlank}>${text}</a>`
        },
        em(text) {
          return locale === 'ja-jp' ? `<strong>${text}</strong>` : `<em>${text}</em>`
        },
      }

      marked.use({ renderer: renderer as Renderer })

      return marked(markdown, { headerIds })
    }, [headerIds, locale, markdown, pageConfigType])

    return (
      <MarkdownWrapper
        skipWidth={skipWidth}
        {...divProps}
        dangerouslySetInnerHTML={{ __html: html }}
        ref={ref}
      />
    )
  }
)
